import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsuranceHeader from "./components/InsuranceHeader";
import InsuranceAddons from "../addons/components/InsuranceAddoons";
import CreateInsuranceAddon from "../addons/CreateInsuranceAddon";
import apiClient from "../../helpers/apiClient";
import { insuranceAddonApi } from "../addons/lib/addonApi";
import { useToast } from "../../contexts/ToastContext";

const Insurance = ({ embedded }) => {
    const { t } = useTranslation(["addons", "insurance"]);
    const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
    const [addonToEditInsurance, setAddonToEditInsurance] = useState(null);
    const [insuranceAddons, setInsuranceAddons] = useState([]);
    const [initialFetching, setInitialFetching] = useState(true);
    const [insurancePagination, setInsurancePagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
    });
    const toast = useToast();

    const fetchInsuranceData = useCallback(
        async (page = 1, limit = 10) => {
            try {
                setInitialFetching(true);
                const response = await apiClient.get(insuranceAddonApi.list(page, limit));
                const newData = response?.data?.data?.result ?? response?.data?.result;
                const totalItems = response?.data?.data?.meta?.total ?? response?.data?.meta?.total ?? 0;
                setInsuranceAddons(newData ?? []);
                setInsurancePagination((prev) => ({ ...prev, total: totalItems }));
            } catch (_error) {
                toast.error({
                    message: t("fetchError"),
                    description: t("failedToFetchAddons"),
                });
            } finally {
                setInitialFetching(false);
            }
        },
        [toast, t],
    );

    const handleInsuranceModalState = (state) => {
        if (typeof state === "object" && state !== null) {
            setIsInsuranceModalOpen(state.isOpen);
            setAddonToEditInsurance(state.addonToEdit ?? null);
        } else {
            setIsInsuranceModalOpen(!!state);
            if (!state) setAddonToEditInsurance(null);
        }
    };

    useEffect(() => {
        fetchInsuranceData(insurancePagination.currentPage, insurancePagination.pageSize);
    }, [fetchInsuranceData, insurancePagination.currentPage, insurancePagination.pageSize]);

    const panelContent = (
        <>
            <InsuranceHeader
                total={insurancePagination.total}
                onCreateInsurance={() => {
                    setAddonToEditInsurance(null);
                    setIsInsuranceModalOpen(true);
                }}
            />

            <InsuranceAddons
                insuranceAddons={insuranceAddons}
                initialFetching={initialFetching}
                handleModalState={handleInsuranceModalState}
                pagination={insurancePagination}
                setPagination={setInsurancePagination}
            />

            <CreateInsuranceAddon
                open={isInsuranceModalOpen}
                onClose={() => handleInsuranceModalState(false)}
                addonToEdit={addonToEditInsurance}
                onSuccess={() => fetchInsuranceData(insurancePagination.currentPage, insurancePagination.pageSize)}
                pagination={insurancePagination}
            />
        </>
    );

    if (embedded) {
        return panelContent;
    }

    return (
        <>
            <div>
                {panelContent}
            </div>
        </>
    );
};

Insurance.propTypes = {
    embedded: PropTypes.bool,
};

Insurance.defaultProps = {
    embedded: false,
};

export default Insurance;
